/**
 * Every word the mockup pages use for where a version stands (session 48).
 *
 * One file for both portals, because the same state is said two ways — to the client it is
 * theirs to answer, to the studio it is the client's — and two pages each choosing their own
 * words is how "Waiting on you" and "Awaiting review" end up on one mockup.
 */

import type { FileReviewState, MockupVersionView } from '@fareast/shared'

import type { PreviewWidth } from '../../components/portal/Files.js'

export type Audience = 'client' | 'studio'

export const WIDTH_WORDS: Readonly<Record<PreviewWidth, string>> = {
  desktop: 'Desktop',
  tablet: 'Tablet',
  phone: 'Phone',
}

/** The pill a version's review wears, or null when nobody asked. `need` is ochre's one meaning. */
export function reviewPill(
  state: FileReviewState,
  audience: Audience,
): { readonly word: string; readonly need: boolean } | null {
  switch (state) {
    case 'requested':
      return audience === 'client'
        ? { word: 'Waiting for your answer', need: true }
        : { word: 'Waiting on the client', need: false }
    case 'approved':
      return { word: 'Approved', need: false }
    case 'changes_requested':
      return { word: 'Changes requested', need: false }
    case 'none':
      return null
  }
}

/** "Version 2". */
export function versionWord(version: Pick<MockupVersionView, 'number'>): string {
  return `Version ${String(version.number)}`
}

/** What an answered review says happened, in a sentence. */
export function answeredLine(
  version: MockupVersionView,
  formatDate: (instant: string) => string,
): string | null {
  const { review } = version
  if (review.answered_at === null) return null
  const who = review.answered_by ?? 'Somebody'
  const what = review.state === 'approved' ? 'approved' : 'asked for changes on'
  return `${who} ${what} ${versionWord(version).toLowerCase()} on ${formatDate(review.answered_at)}.`
}

/* ------------------------------------------------------------- the review (55) */

/** Said beside every comment box: the owner's choice was that the link reads them all (49). */
export const COMMENTS_ARE_SHARED =
  'Anybody with the mockup’s link can read these comments, and add their own.'

/** How to start a comment, said once in the list — the three gestures, shortest first. */
export const GESTURE_HINT =
  'Select text, or right-click anywhere on the page, to comment on it. Or press C, then click a ' +
  'spot or drag over an area.'

/** Under a comment box: what Enter does. */
export const ENTER_HINT = 'Enter posts · Shift+Enter for a new line'

/** What a thread is about, in a few words — or null for a plain spot, which its pin says. */
export function aboutWords(
  pin: { readonly quote?: string | undefined; readonly w?: number | undefined } | null,
): string | null {
  if (pin === null) return 'The whole version'
  if (pin.quote !== undefined) return null
  return pin.w === undefined ? null : 'An area of the page'
}

/** "2 replies". */
export function repliesWord(count: number): string {
  return count === 1 ? '1 reply' : `${String(count)} replies`
}
